
import { Button, FormControl, IconButton, InputLabel, Select, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded as AddIcon, DeleteRounded as DeleteIcon } from '@material-ui/icons';

import { DeliveryPointSchedule } from '../../types/delivery-point.type';
import { StorableDay } from '../../types/delivery-point-day.type';
import { UpdateDeliveryPointSchedule } from '../../types/dtos/update-delivery-point.dto';
import { STORABLE_DAY_OPTIONS } from '../../utils/format-schedule-days.util';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  addButton: {
    alignSelf: 'flex-start',
  },
}));

const PICKUP_REQUIRED_ERROR = 'Les deux bornes de retrait sont obligatoires';
const DEPOSIT_REQUIRED_ERROR = 'Un dépôt exige ses deux bornes';

// Un dépôt à moitié rempli est CONSERVÉ pour que la saisie s'affiche : l'éditeur est contrôlé,
// refuser d'émettre reviendrait à refuser la frappe. C'est l'enregistrement qui est bloqué, par
// `hasIncompleteScheduleBounds` ci-dessous — l'API accepte une borne vide et le mapper fait
// disparaître le dépôt entier à la lecture suivante.
const buildDeposit = (from: string, to: string) => (from || to ? { from, to } : undefined);

export const hasIncompleteScheduleBounds = (schedules: DeliveryPointSchedule[]): boolean =>
  schedules.some(
    (schedule) =>
      !schedule.pickup.from ||
      !schedule.pickup.to ||
      (schedule.deposit !== undefined && (!schedule.deposit.from || !schedule.deposit.to))
  );

const DEFAULT_SLOT = { from: '09:00', to: '12:00' };

const buildDefaultSchedule = (): UpdateDeliveryPointSchedule => ({
  day: STORABLE_DAY_OPTIONS[0].value,
  pickup: { ...DEFAULT_SLOT },
  deposit: { ...DEFAULT_SLOT },
});

// `DeliveryPointSchedule.day` reste `string` en lecture par tolérance aux documents historiques
// (voir `delivery-point-day.type.ts`) ; les options proposées ici excluent `sunday`, donc toute
// ligne non modifiée par l'utilisateur est renvoyée telle quelle, day inclus.
const toStorableSchedule = (schedule: DeliveryPointSchedule): UpdateDeliveryPointSchedule => ({
  ...schedule,
  day: schedule.day as StorableDay,
});

type Props = {
  schedules: DeliveryPointSchedule[];
  onChange: (schedules: UpdateDeliveryPointSchedule[]) => void;
};

export default function DeliveryPointScheduleEditor({ schedules, onChange }: Props) {
  const classes = useStyles();
  const emitRowChange = (changedIndex: number, changedSchedule: UpdateDeliveryPointSchedule) => {
    onChange(
      schedules.map((schedule, index) => (index === changedIndex ? changedSchedule : toStorableSchedule(schedule)))
    );
  };

  const handlePickupChange = (
    changedIndex: number,
    storableSchedule: UpdateDeliveryPointSchedule,
    bound: 'from' | 'to',
    rawValue: string
  ) => {
    emitRowChange(changedIndex, {
      ...storableSchedule,
      pickup: { ...storableSchedule.pickup, [bound]: rawValue },
    });
  };

  const handleDelete = (removedIndex: number) => {
    onChange(
      schedules.filter((_schedule, index) => index !== removedIndex).map(toStorableSchedule)
    );
  };

  const handleAdd = () => {
    onChange([...schedules.map(toStorableSchedule), buildDefaultSchedule()]);
  };

  return (
    <div className={classes.root}>
      {schedules.map((schedule, index) => {
        const storableSchedule = toStorableSchedule(schedule);
        const isPickupIncomplete = !schedule.pickup.from || !schedule.pickup.to;
        const isDepositIncomplete =
          schedule.deposit !== undefined && (!schedule.deposit.from || !schedule.deposit.to);

        return (
          <div className={classes.row} key={index}>
            <FormControl variant="outlined" size="small">
              <InputLabel htmlFor={`schedule-${index}-day`}>Jour</InputLabel>
              <Select
                native
                label="Jour"
                id={`schedule-${index}-day`}
              value={schedule.day}
                onChange={(event) =>
                  emitRowChange(index, { ...storableSchedule, day: event.target.value as StorableDay })
                }
              >
                {STORABLE_DAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <TextField
              id={`schedule-${index}-pickup-from`}
              label="Retrait de"
              type="time"
              size="small"
              variant="outlined"
              error={isPickupIncomplete}
              value={schedule.pickup.from}
              onChange={(event) => handlePickupChange(index, storableSchedule, 'from', event.target.value)}
            />
            <TextField
              id={`schedule-${index}-pickup-to`}
              label="Retrait à"
              type="time"
              size="small"
              variant="outlined"
              error={isPickupIncomplete}
              helperText={isPickupIncomplete ? PICKUP_REQUIRED_ERROR : undefined}
              value={schedule.pickup.to}
              onChange={(event) => handlePickupChange(index, storableSchedule, 'to', event.target.value)}
            />
            <TextField
              id={`schedule-${index}-deposit-from`}
              label="Dépôt de"
              type="time"
              size="small"
              variant="outlined"
              error={isDepositIncomplete}
              value={schedule.deposit?.from ?? ''}
              onChange={(event) =>
                emitRowChange(index, {
                  ...storableSchedule,
                  deposit: buildDeposit(event.target.value, schedule.deposit?.to ?? ''),
                })
              }
            />
            <TextField
              id={`schedule-${index}-deposit-to`}
              label="Dépôt à"
              type="time"
              size="small"
              variant="outlined"
              error={isDepositIncomplete}
              helperText={isDepositIncomplete ? DEPOSIT_REQUIRED_ERROR : undefined}
              value={schedule.deposit?.to ?? ''}
              onChange={(event) =>
                emitRowChange(index, {
                  ...storableSchedule,
                  deposit: buildDeposit(schedule.deposit?.from ?? '', event.target.value),
                })
              }
            />
            <IconButton size="small" title="Supprimer la ligne" onClick={() => handleDelete(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        );
      })}
      <Button size="small" variant="outlined" className={classes.addButton} startIcon={<AddIcon />} onClick={handleAdd}>
        Ajouter un horaire
      </Button>
    </div>
  );
}
